import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Castle, CheckCircle2, Timer, Users, Camera, Heart, Trophy, X, Mail, Lock, LogIn, Star, Rocket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { trackLogin, trackSignup } from '../analytics';

// Deterministic star field — avoids hydration mismatches
const STARS = Array.from({ length: 80 }, (_, i) => ({
    left: `${((i * 1664525 + 1013904223) % 10000) / 100}%`,
    top: `${((i * 22695477 + 1103515245) % 10000) / 100}%`,
    large: i % 9 === 0,
    dim: i % 4 === 0,
    duration: `${2 + (i % 4)}s`,
    delay: `${(i % 20) * 0.15}s`,
}));

const FEATURES = [
    {
        icon: <CheckCircle2 className="h-7 w-7" />,
        color: 'text-emerald-400',
        bg: 'bg-emerald-400/10',
        border: 'border-emerald-400/20',
        title: 'Track Every Attraction',
        desc: 'Check off rides, shows, character meets and hotels as you experience them — across both parks.',
    },
    {
        icon: <Timer className="h-7 w-7" />,
        color: 'text-blue-400',
        bg: 'bg-blue-400/10',
        border: 'border-blue-400/20',
        title: 'Live Wait Times',
        desc: 'Real-time queue lengths for every ride so you can always be in the right place at the right time.',
    },
    {
        icon: <Users className="h-7 w-7" />,
        color: 'text-violet-400',
        bg: 'bg-violet-400/10',
        border: 'border-violet-400/20',
        title: 'Family Profiles',
        desc: "Everyone gets their own profile to track their personal journey through the parks.",
    },
    {
        icon: <Camera className="h-7 w-7" />,
        color: 'text-pink-400',
        bg: 'bg-pink-400/10',
        border: 'border-pink-400/20',
        title: 'Photo Memories',
        desc: 'Attach a photo to every attraction you conquer. Relive the magic long after you get home.',
    },
    {
        icon: <Heart className="h-7 w-7" />,
        color: 'text-rose-400',
        bg: 'bg-rose-400/10',
        border: 'border-rose-400/20',
        title: 'Wishlist',
        desc: 'Build your must-do list before the trip so nothing gets missed on the day.',
    },
    {
        icon: <Trophy className="h-7 w-7" />,
        color: 'text-amber-400',
        bg: 'bg-amber-400/10',
        border: 'border-amber-400/20',
        title: 'Family Leaderboard',
        desc: "A little friendly competition — see who's conquered the most attractions in the group.",
    },
];

const STEPS = [
    { n: '1', title: 'Create your free account', desc: 'Sign up with Google or email in one click — no credit card needed.' },
    { n: '2', title: 'Add your family', desc: 'Set up a profile for everyone joining the adventure.' },
    { n: '3', title: 'Start tracking', desc: 'Check off rides, view live queues and capture memories as you go.' },
];

export function LandingPage() {
    const [showAuth, setShowAuth] = useState(false);
    const [isSignup, setIsSignup] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, signup, loginWithGoogle } = useAuth();

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (showAuth) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showAuth]);

    function openLogin() { setIsSignup(false); setShowAuth(true); setError(''); }
    function openSignup() { setIsSignup(true); setShowAuth(true); setError(''); }
    function closeAuth() { setShowAuth(false); setError(''); setEmail(''); setPassword(''); }

    async function handleSubmit(e) {
        e.preventDefault();
        try {
            setError('');
            setLoading(true);
            if (isSignup) {
                await signup(email, password);
                trackSignup('email');
            } else {
                await login(email, password);
                trackLogin('email');
            }
        } catch {
            setError('Failed to ' + (isSignup ? 'create an account' : 'sign in') + '. Please check your credentials and try again.');
        }
        setLoading(false);
    }

    async function handleGoogleLogin() {
        try {
            setError('');
            setLoading(true);
            await loginWithGoogle();
            trackLogin('google');
        } catch {
            setError('Failed to sign in with Google. Please try again.');
        }
        setLoading(false);
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">

            {/* ── HERO ─────────────────────────────────────────────────── */}
            <section className="relative min-h-screen flex flex-col overflow-hidden">
                {/* Sky gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0520] via-[#150a38] via-50% to-slate-950" />

                {/* Starfield */}
                <div className="absolute inset-0 pointer-events-none">
                    {STARS.map((s, i) => (
                        <div
                            key={i}
                            className={`absolute rounded-full bg-white animate-pulse
                                ${s.large ? 'w-1 h-1' : 'w-px h-px'}
                                ${s.dim ? 'opacity-20' : 'opacity-60'}`}
                            style={{ left: s.left, top: s.top, animationDuration: s.duration, animationDelay: s.delay }}
                        />
                    ))}
                </div>

                {/* Glow orbs */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-indigo-700/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

                {/* Nav */}
                <nav className="relative z-10 flex items-center justify-between px-6 py-5 max-w-6xl mx-auto w-full">
                    <div className="flex items-center gap-2">
                        <Castle className="h-6 w-6 text-indigo-400" />
                        <span className="font-bold text-lg tracking-tight text-white">DLP Tracker</span>
                    </div>
                    <button
                        onClick={openLogin}
                        className="text-slate-300 hover:text-white text-sm font-medium transition-colors px-4 py-2 rounded-lg hover:bg-white/5"
                    >
                        Sign In
                    </button>
                </nav>

                {/* Hero content */}
                <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pb-28 pt-4">
                    {/* Castle with glow */}
                    <div className="relative mb-8 inline-block">
                        <div className="absolute inset-0 bg-indigo-400/30 rounded-full blur-2xl scale-[2]" />
                        <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-3xl scale-[3]" />
                        <Castle className="relative h-24 w-24 sm:h-32 sm:w-32 text-indigo-300 drop-shadow-2xl" />
                        {/* Sparkle stars around castle */}
                        <Star className="absolute -top-3 -right-2 h-4 w-4 text-amber-300 fill-amber-300 animate-pulse" style={{ animationDuration: '1.5s' }} />
                        <Star className="absolute top-2 -left-4 h-3 w-3 text-amber-200 fill-amber-200 animate-pulse" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
                        <Star className="absolute -bottom-1 right-0 h-3 w-3 text-indigo-300 fill-indigo-300 animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '1s' }} />
                    </div>

                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 bg-indigo-500/15 border border-indigo-400/25 rounded-full px-4 py-1.5 text-sm text-indigo-300 mb-6">
                        ✨ Your Disneyland Paris companion
                    </div>

                    <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 max-w-4xl leading-[1.1]">
                        Make every{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-400 to-purple-400">
                            magical moment
                        </span>{' '}
                        count
                    </h1>

                    <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mb-10 leading-relaxed">
                        Track rides, check live wait times, build wishlists and capture photos — the ultimate
                        family companion for your Disneyland Paris holiday.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <button
                            onClick={openSignup}
                            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-4 px-9 rounded-2xl text-lg transition-all duration-200 shadow-xl shadow-indigo-600/30 hover:shadow-indigo-500/50 hover:-translate-y-0.5 active:translate-y-0"
                        >
                            Start Tracking — It's Free
                        </button>
                        <button
                            onClick={openLogin}
                            className="bg-white/8 hover:bg-white/12 text-white font-semibold py-4 px-9 rounded-2xl text-lg transition-all duration-200 border border-white/15 hover:-translate-y-0.5 active:translate-y-0 backdrop-blur-sm"
                        >
                            Sign In
                        </button>
                    </div>

                    <p className="text-slate-500 text-sm">Free for families · No app download needed · Works on any device</p>
                </div>

                {/* Scroll hint */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
                    <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
                        <div className="w-1 h-2 rounded-full bg-white/40" />
                    </div>
                </div>
            </section>

            {/* ── PARKS STRIP ──────────────────────────────────────────── */}
            <div className="bg-indigo-900/30 border-y border-indigo-800/30 py-4 px-6">
                <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-slate-300 text-sm font-medium">
                    <span>🏰 Disneyland Park</span>
                    <span>🎬 Disney Adventure World</span>
                    <span>🏨 Disney Hotels</span>
                    <span>🎭 Shows &amp; Entertainment</span>
                    <span>⏱️ Live Queue Times</span>
                </div>
            </div>

            {/* ── VISUAL PARK SHOWCASE ──────────────────────────────────── */}
            <section className="py-16 px-6 bg-slate-950">
                <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Disneyland Park card */}
                    <div className="sm:col-span-2 relative rounded-3xl overflow-hidden h-64 sm:h-80 group">
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        {/* Decorative castle silhouette */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                            <Castle className="w-48 h-48 text-indigo-200" />
                        </div>
                        <div className="absolute inset-0 flex items-end p-6">
                            <div>
                                <span className="text-xs font-semibold uppercase tracking-widest text-indigo-300 mb-1 block">Park One</span>
                                <h3 className="text-2xl font-extrabold text-white">Disneyland Park</h3>
                                <p className="text-slate-300 text-sm mt-1">Home of Sleeping Beauty Castle</p>
                            </div>
                        </div>
                        {/* Stars overlay */}
                        <div className="absolute top-4 right-4 flex gap-1">
                            {[...Array(3)].map((_, i) => (
                                <Star key={i} className="h-3 w-3 text-amber-300 fill-amber-300 animate-pulse" style={{ animationDelay: `${i * 0.4}s` }} />
                            ))}
                        </div>
                    </div>

                    {/* Disney Adventure World card */}
                    <div className="relative rounded-3xl overflow-hidden h-64 sm:h-80 group">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-cyan-900 to-slate-900" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                            <Rocket className="w-32 h-32 text-blue-200" />
                        </div>
                        <div className="absolute inset-0 flex items-end p-6">
                            <div>
                                <span className="text-xs font-semibold uppercase tracking-widest text-blue-300 mb-1 block">Park Two</span>
                                <h3 className="text-xl font-extrabold text-white">Disney Adventure World</h3>
                                <p className="text-slate-300 text-sm mt-1">Thrills for the whole family</p>
                            </div>
                        </div>
                    </div>

                    {/* Hotels card */}
                    <div className="relative rounded-3xl overflow-hidden h-48 group">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-900 via-orange-900 to-slate-900" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div className="absolute inset-0 flex items-end p-5">
                            <div>
                                <span className="text-xs font-semibold uppercase tracking-widest text-amber-300 mb-1 block">Stay</span>
                                <h3 className="text-lg font-extrabold text-white">Disney Hotels</h3>
                                <p className="text-slate-300 text-sm">Track your hotel stay too</p>
                            </div>
                        </div>
                    </div>

                    {/* Wait times card */}
                    <div className="relative rounded-3xl overflow-hidden h-48 group">
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-25">
                            <Timer className="w-24 h-24 text-emerald-200" />
                        </div>
                        <div className="absolute inset-0 flex items-end p-5">
                            <div>
                                <div className="flex gap-2 mb-2">
                                    <span className="text-xs bg-emerald-500 text-white rounded-full px-2 py-0.5 font-semibold">5 min</span>
                                    <span className="text-xs bg-amber-500 text-white rounded-full px-2 py-0.5 font-semibold">25 min</span>
                                    <span className="text-xs bg-red-500 text-white rounded-full px-2 py-0.5 font-semibold">60 min</span>
                                </div>
                                <h3 className="text-lg font-extrabold text-white">Live Wait Times</h3>
                            </div>
                        </div>
                    </div>

                    {/* Memories card */}
                    <div className="relative rounded-3xl overflow-hidden h-48 group">
                        <div className="absolute inset-0 bg-gradient-to-br from-pink-900 via-rose-900 to-slate-900" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-25">
                            <Camera className="w-24 h-24 text-pink-200" />
                        </div>
                        <div className="absolute inset-0 flex items-end p-5">
                            <div>
                                <h3 className="text-lg font-extrabold text-white">Photo Memories</h3>
                                <p className="text-slate-300 text-sm">A photo for every moment</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FEATURES GRID ────────────────────────────────────────── */}
            <section className="py-24 px-6 bg-gradient-to-b from-slate-950 to-slate-900">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Everything you need for the perfect visit</h2>
                        <p className="text-slate-400 text-lg max-w-xl mx-auto">Six features built for families who want to make the most of every single minute in the parks.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {FEATURES.map((f) => (
                            <div key={f.title} className={`bg-slate-800/50 rounded-2xl p-6 border ${f.border} hover:bg-slate-800/80 transition-colors`}>
                                <div className={`inline-flex p-3 rounded-xl ${f.bg} ${f.color} mb-4`}>
                                    {f.icon}
                                </div>
                                <h3 className="text-lg font-bold mb-2 text-white">{f.title}</h3>
                                <p className="text-slate-400 leading-relaxed text-sm">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── HOW IT WORKS ─────────────────────────────────────────── */}
            <section className="py-24 px-6 bg-gradient-to-b from-slate-900 to-indigo-950/40">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-extrabold mb-3">Up and running in seconds</h2>
                    <p className="text-slate-400 text-lg mb-16">No app store. No download. Works on any phone, tablet or laptop.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
                        {STEPS.map((s, i) => (
                            <div key={s.n} className="flex flex-col items-center">
                                <div className="relative mb-5">
                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl font-extrabold shadow-xl shadow-indigo-500/30">
                                        {s.n}
                                    </div>
                                    {i < STEPS.length - 1 && (
                                        <div className="hidden sm:block absolute top-1/2 left-full w-full h-px border-t-2 border-dashed border-indigo-800 -translate-y-1/2 translate-x-2" style={{ width: 'calc(100% + 2.5rem)' }} />
                                    )}
                                </div>
                                <h3 className="font-bold text-lg mb-2 text-white">{s.title}</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FINAL CTA ────────────────────────────────────────────── */}
            <section className="py-24 px-6 bg-slate-950">
                <div className="max-w-2xl mx-auto text-center bg-gradient-to-b from-indigo-900/40 to-purple-900/30 rounded-3xl border border-indigo-500/20 p-12 relative overflow-hidden">
                    {/* Background glow */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-purple-600/5 pointer-events-none" />
                    <div className="relative">
                        <div className="flex items-center justify-center gap-1 mb-6">
                            <Star className="h-5 w-5 text-amber-300 fill-amber-300" />
                            <Star className="h-6 w-6 text-amber-300 fill-amber-300" />
                            <Castle className="h-10 w-10 text-indigo-300 mx-2" />
                            <Star className="h-6 w-6 text-amber-300 fill-amber-300" />
                            <Star className="h-5 w-5 text-amber-300 fill-amber-300" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Your adventure begins here</h2>
                        <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                            Join families using DLP Tracker to make their Disneyland Paris holiday truly unforgettable.
                        </p>
                        <button
                            onClick={openSignup}
                            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-4 px-10 rounded-2xl text-lg transition-all duration-200 shadow-xl shadow-indigo-500/30 hover:-translate-y-0.5"
                        >
                            Create Free Account
                        </button>
                        <p className="text-slate-500 text-sm mt-4">No credit card · Free forever</p>
                    </div>
                </div>
            </section>

            {/* ── FOOTER ───────────────────────────────────────────────── */}
            <footer className="border-t border-slate-800/60 py-8 px-6 text-center text-slate-500 text-sm">
                <div className="flex items-center justify-center gap-2 mb-1">
                    <Castle className="h-4 w-4 text-indigo-500" />
                    <span className="font-semibold text-slate-400">DLP Tracker</span>
                </div>
                <p>Made with ❤️ for Disneyland Paris families · Not affiliated with The Walt Disney Company</p>
            </footer>

            {/* ── AUTH MODAL ───────────────────────────────────────────── */}
            {showAuth && createPortal(
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={(e) => e.target === e.currentTarget && closeAuth()}
                >
                    <div className="bg-slate-800 rounded-2xl shadow-2xl p-8 max-w-md w-full border border-slate-700 relative">
                        <button onClick={closeAuth} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors">
                            <X className="h-5 w-5" />
                        </button>

                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Castle className="h-6 w-6 text-indigo-400" />
                            <span className="font-bold text-xl">DLP Tracker</span>
                        </div>
                        <h2 className="text-2xl font-bold text-center mb-6 text-white">
                            {isSignup ? 'Create your account' : 'Welcome back'}
                        </h2>

                        {error && (
                            <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-xl mb-4 text-sm">
                                {error}
                            </div>
                        )}

                        {/* Google sign-in */}
                        <button
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            className="w-full bg-white hover:bg-gray-100 disabled:opacity-60 text-gray-900 font-semibold py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 mb-4"
                        >
                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="h-5 w-5" />
                            {isSignup ? 'Sign up with Google' : 'Sign in with Google'}
                        </button>

                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1 h-px bg-slate-700" />
                            <span className="text-slate-500 text-xs uppercase tracking-wider">or</span>
                            <div className="flex-1 h-px bg-slate-700" />
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-3">
                            <div className="relative">
                                <Mail className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                                <input
                                    type="email"
                                    placeholder="Email address"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 p-3 text-white focus:outline-none focus:border-indigo-500 placeholder-slate-500 transition-colors"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                                <input
                                    type="password"
                                    placeholder="Password"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 p-3 text-white focus:outline-none focus:border-indigo-500 placeholder-slate-500 transition-colors"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white font-bold py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 mt-1"
                            >
                                <LogIn className="h-5 w-5" />
                                {loading ? 'Please wait…' : isSignup ? 'Create Account' : 'Sign In'}
                            </button>
                        </form>

                        <p className="text-center text-slate-400 text-sm mt-4">
                            {isSignup ? 'Already have an account?' : "Don't have an account?"}
                            <button
                                onClick={() => { setIsSignup(!isSignup); setError(''); }}
                                className="text-indigo-400 hover:text-indigo-300 ml-1 font-semibold transition-colors"
                            >
                                {isSignup ? 'Sign In' : 'Sign Up Free'}
                            </button>
                        </p>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
